const DB_NAME = 'duckpilot_query_history';
const STORE_NAME = 'history';
const DB_VERSION = 1;

const isSupported = () => typeof indexedDB !== 'undefined';

const openDatabase = () => new Promise((resolve, reject) => {
  if (!isSupported()) {
    reject(new Error('IndexedDB is not available'));
    return;
  }

  const request = indexedDB.open(DB_NAME, DB_VERSION);

  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  };

  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const saveQuery = async (record) => {
  if (!isSupported()) return;
  const db = await openDatabase();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Failed to save query history'));
    tx.objectStore(STORE_NAME).put(record);
  });
};

const loadAllQueries = async () => {
  if (!isSupported()) return [];
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error || new Error('Failed to load query history'));
  });
};

const loadQueriesByDataset = async (datasetId) => {
  const all = await loadAllQueries();
  return all.filter((item) => item.dataset_id === datasetId);
};

const updateQuery = async (id, updates) => {
  if (!isSupported()) return;
  const db = await openDatabase();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const record = getRequest.result;
      if (!record) {
        reject(new Error('Query history record not found'));
        return;
      }
      store.put({ ...record, ...updates });
    };
    getRequest.onerror = () => reject(getRequest.error || new Error('Failed to update query history'));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Failed to update query history'));
  });
};

const clearAllQueries = async () => {
  if (!isSupported()) return;
  const db = await openDatabase();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('Failed to clear query history'));
    tx.objectStore(STORE_NAME).clear();
  });
};

const queryHistoryStorage = {
  isSupported,
  saveQuery,
  loadAllQueries,
  loadQueriesByDataset,
  updateQuery,
  clearAllQueries
};

export default queryHistoryStorage;
